// src/v2/hooks/useBIR0619E.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useBIR0619E = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Internal helper to write text to specific PDF coordinates
   */
  const writeText = (page, font, x, y, text, size = 10) => {
    if (!text) return;
    page.drawText(text.toString(), {
      x: x,
      y: y,
      size: size,
      font: font,
      color: rgb(0, 0, 0),
    });
  };

  /**
   * Scenario A: Download the raw template directly
   */
  const downloadBlankTemplate = useCallback((month, year) => {
    // MODIFIED: Path updated to match your file: BIR0619-E.pdf
    const pdfUrl = `${window.location.origin}/docs/BIR0619-E.pdf`;

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `BIR0619-E_${month}_${year}_Blank.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showGeneralMessage("Blank template downloaded (No records found).", true);
  }, [showGeneralMessage]);

  /**
   * Scenario B: Fill the template with data
   */
  const fillAndDownloadPDF = useCallback(async (data, month, year, companyInfo) => {
    try {
      // MODIFIED: Path updated to match your file: BIR0619-E.pdf
      const pdfUrl = `${window.location.origin}/docs/BIR0619-E.pdf`;

      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error("Template file not found at " + pdfUrl);

      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const page = pdfDoc.getPages()[0];
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // --- COORDINATE MAPPING ---
      // Box 1: Month and Year
      const mm = month.padStart(2, '0');
      writeText(page, font, 73, 818, mm[0]);
      writeText(page, font, 88, 818, mm[1]);
      const yyyy = year.toString();
      writeText(page, font, 108, 818, yyyy[0]);
      writeText(page, font, 123, 818, yyyy[1]);
      writeText(page, font, 138, 818, yyyy[2]);
      writeText(page, font, 153, 818, yyyy[3]);

      // Box 7: TIN
      if (companyInfo?.tin) {
        const tinDigits = companyInfo.tin.replace(/\D/g, '');
        let startX = 238;
        for (let i = 0; i < Math.min(tinDigits.length, 9); i++) {
          writeText(page, font, startX + (i * 18.5), 785, tinDigits[i]);
          if ((i + 1) % 3 === 0) startX += 5;
        }
      }

      // Box 9: Agent Name
      writeText(page, font, 25, 758, companyInfo?.name || "", 9);

      // Box 10: Address
      writeText(page, font, 25, 732, companyInfo?.address || "", 8);

      // Part II: Computation
      const totalTax = data.reduce((sum, item) => sum + (item.taxWithheld || 0), 0);
      const taxStr = totalTax.toLocaleString('en-US', { minimumFractionDigits: 2 });
      writeText(page, font, 480, 633, taxStr); // Line 14
      writeText(page, font, 480, 550, taxStr); // Line 18

      // Serialize and Download
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      // MODIFIED: Updated Download Filename
      link.download = `BIR0619-E_${month}_${year}.pdf`;
      link.click();

      showGeneralMessage("Report generated and downloaded.", true);
    } catch (error) {
      console.error(error);
      showGeneralMessage("Error filling PDF. Downloading blank instead.", false);
      downloadBlankTemplate(month, year);
    }
  }, [showGeneralMessage, downloadBlankTemplate]);

  const generateAndDownload = useCallback(async (params, month, year, companyInfo) => {
    setLoading(true);
    try {
      let data = [];
      try {
        const res = await reportsService.getReportData('BIR0619E', params);
        data = res || [];
      } catch (apiErr) {
        data = []; // Fallback for demo
      }

      if (data.length === 0) {
        downloadBlankTemplate(month, year);
      } else {
        await fillAndDownloadPDF(data, month, year, companyInfo);
      }

    } catch (err) {
      showGeneralMessage("Failed to process request.", false);
    } finally {
      setLoading(false);
    }
  }, [fillAndDownloadPDF, downloadBlankTemplate, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};